
import useEmblaCarousel from 'embla-carousel-react';
import { Children, cloneElement, useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import styled from 'styled-components';
import devices from '@/utilities/devices';

// #region STYLE
const CarouselWrapper = styled.div`
	position: relative;
	.is-draggable {
		cursor: move;
		cursor: grab;
	}
	.is-dragging {
		cursor: grabbing;
	}
`;

const CarouselContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
	margin: 2rem auto;
	overflow: hidden;
	width: 90%;
	.content-container {
		display: flex;
		user-select: none;
		-webkit-touch-callout: none;
		-khtml-user-select: none;
		-webkit-tap-highlight-color: transparent;
		width: 100%;
	}

	@media screen and (${devices.mobile}) {
		width: 100%;
	}

	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		width: 100%;
	}
	/* @media screen and (min-width: 1439px) {
		width: 75%;
	} */
`;

const CarouselDotContainer = styled.div`
	display: flex;
	justify-content: center;

	@media screen and (${devices.mobile}) {
		margin-top: 2rem;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		margin-top: 4rem;
	}
`;

const CarouselDot = styled.div<{ isSelected: boolean }>`
	background-color: ${({ theme, isSelected }) => isSelected ? theme.color.prune : theme.color.pruneSuperLight};
	border-radius: 60px;
	cursor: pointer;
	height: 15px;
	margin: 0 .4rem;
	transition: .4s;
	width: 15px;
`;

const CarouselNavigationButton = styled.div<{ position: 'left' | 'right' }>`
	align-items: center;
	background-color: ${({ theme }) => theme.color.white};
	border: 1px solid ${({ theme }) => theme.color.grey};
	border-radius: 50px;
	cursor: pointer;
	display: flex;
	height: 36px;
	justify-content: center;
	left: ${({ position }) => position === 'left' && '-10px'};
	transition: .3s;
	width: 36px;
	position: absolute;
	right: ${({ position }) => position === 'right' && '-10px'};
	top: 50%;

	svg {
		font-size: 1.25rem;
		transform: ${({ position }) => position === 'left' ? 'translateX(-1px)' : 'translateX(1px)'}
	}

	:hover {
		background-color: ${({ theme }) => theme.color.greyLight};
		transform: ${({ position }) => position === 'left' ? 'translateX(-4px)' : 'translateX(4px)'}
	}

	@media screen and (${devices.mobile}) {
		top: 28%;
	}
`;
// #endregion

// #region COMPONENT
const Carousel = ({ children }: any) => {

	const [emblaRef, embla] = useEmblaCarousel({
		loop: true,
		skipSnaps: false,
		speed: 15,
	});
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const scrollPrev = useCallback(
		() => embla && embla.scrollPrev(),
		[embla],
	);
	const scrollNext = useCallback(
		() => embla && embla.scrollNext(),
		[embla],
	);
	const scrollTo = useCallback(
		(index: number) => embla && embla.scrollTo(index, true),
		[embla],
	);
	const onSelect = useCallback(
		() => {
			if (!embla) {
				return;
			}
			setSelectedIndex(embla.selectedScrollSnap());
		},
		[embla, setSelectedIndex],
	);

	useEffect(
		() => {
			if (!embla) {
				return;
			}
			onSelect();
			setScrollSnaps(embla.scrollSnapList());
			embla.on('select', onSelect);
		},
		[embla, setScrollSnaps, onSelect],
	);

	// #region RENDER
	return (
		<CarouselWrapper>

			<CarouselContainer ref={emblaRef} className='is-draggable'>
				<div className='content-container'>
					{
						Children.map(children, ((child: any) => (
							cloneElement(child, { isSelected: Number(child.key) === selectedIndex })
						)))
					}
				</div>

				<CarouselDotContainer>
					{scrollSnaps.map((_, index) => (
						<CarouselDot
							isSelected={index === selectedIndex}
							key={index}
							onClick={() => scrollTo(index)}
						/>
					))}
				</CarouselDotContainer>
			</CarouselContainer>

			<CarouselNavigationButton onClick={scrollPrev} position='left'>
				<FiChevronLeft />
			</CarouselNavigationButton>
			<CarouselNavigationButton onClick={scrollNext} position='right'>
				<FiChevronRight />
			</CarouselNavigationButton>
		</CarouselWrapper>
	);
	// #endregion
};
// #endregion

export default Carousel;
